import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';

import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import * as XLSX from '@e965/xlsx';
import { discardLinkDialogData } from '@features/assessments/guards/discard-link-data.guard';
import { ConfirmDialogComponent, ConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DividerComponent } from '@shared/components/divider.component';
import { ImprovedSelectComponent } from '@shared/components/improved-select/improved-select.component';
import { TitleContainerComponent } from '@shared/components/title-container.component';
import { ESG_RISK_SOURCE_SYSTEM, OTHER_COMPANY_ID, OTHER_PROJECT_ID } from '@shared/constants';
import { SdlIdMdmIdName } from '@shared/models/SdlIdMdmIdName.model';
import { SdlIdName } from '@shared/models/sdlIdName.model';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { downloadFileFromAssetFolder } from '@shared/utils/file.utils';
import { maxSelectionValidator } from '@shared/validators/max-selection.validator';
import { requiredTrimmed } from '@shared/validators/required-trimmed.validator';
import { BehaviorSubject, filter, map, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SnackbarWithLinksComponent, SnackWithLinksData } from '../components/snackbar-with-links.component';
import { InsightsAccessBannerComponent } from '../insights/insights-access-banner.component';
import { AssessmentCreation } from '../models/assessment.model';
import { AssessmentsApiService } from '../services/assessments-api.service';
import { AssessmentCreationStore } from '../stores/new-assessment.store';

@Component({
  selector: 'app-new-assessment',
  imports: [
    TitleContainerComponent,
    DividerComponent,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatProgressBarModule,
    ImprovedSelectComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    ScrollingModule,
    InsightsAccessBannerComponent,
  ],
  providers: [AssessmentCreationStore, AssessmentsApiService],
  templateUrl: './new-assessment.component.html',
  styleUrl: './new-assessment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewAssessmentComponent implements OnDestroy {
  readonly OTHER_COMPANY_ID = OTHER_COMPANY_ID;
  readonly OTHER_PROJECT_ID = OTHER_PROJECT_ID;
  readonly MAX_SELECTION = 20;
  readonly SOURCE_SYSTEM = ESG_RISK_SOURCE_SYSTEM;
  private readonly MAX_COORDINATES = 1000;

  readonly PA_TOOLTIP_TEXT = `The protected area check verifies whether submitted locations fall within protected areas. Swiss Re has no risk appetite for projects or transactions that could cause conversion or degradation of these areas.

    - You can run a single-location check or a bulk check.
    - For bulk checks, use the provided Excel template (up to 1,000 locations).
    - If coordinates are unavailable or the check was already done in CatNet, proceed with the assessment without repeating the check.`;

  private readonly fb = inject(FormBuilder);
  readonly store = inject(AssessmentCreationStore);
  readonly router = inject(Router);
  readonly messageService = inject(SnackBarService);

  private readonly dialog = inject(MatDialog);
  private activeDialogRef?: MatDialogRef<ConfirmDialogComponent, boolean>;

  isInitDataLoading = this.store.isInitDataLoading;
  isCompanyLoading = this.store.isCompanyLoading;
  isProjectLoading = this.store.isProjectLoading;

  isCreationLoading = computed(() => this.store.creationStatus() === 'pending');
  isSubmittedSuccessfully = computed(() => this.store.creationStatus() === 'success');

  isBulkCoordinatesLoading = signal(false);

  countries = this.store.countries;
  sectors = this.store.sectors;
  companies = this.store.companies;
  projects = this.store.projects;
  toolTips = this.store.tooltips;

  assessmentForm = this.fb.group({
    dealSourceSystem: [{ value: this.SOURCE_SYSTEM, disabled: true }],
    dealUrl: [{ value: '', disabled: true }],
    dealSourceSystemId: [''],
    isLH: [false],
    isThirdParty: [false],
    countries: [[] as SdlIdName[], [Validators.required, maxSelectionValidator(this.MAX_SELECTION)]],
    sector: [undefined as SdlIdMdmIdName | undefined, Validators.required],
    company: [{ value: undefined as SdlIdName | undefined, disabled: true }, Validators.required],
    otherCompany: [''],
    project: [{ value: undefined as SdlIdName | undefined, disabled: true }, Validators.required],
    otherProject: [''],
    protectedAreaCoordinates: this.fb.array([]),
  });

  // inputs from url query params
  DealID = input<string>();
  Country = input<string>();
  Industry = input<string>(); // in old tool... for now used both Industry and Sector
  Sector = input<string>();
  Company = input<string>();
  otherCompany = input<string>();
  Project = input<string>();
  otherProject = input<string>();

  DealSource = input<string>();
  dealUrl = input<string>();
  pcbpmProcessId = input<string>();
  mainLineOfBusinessId = input<string>();

  hasLinkData = computed(() => {
    const values = [this.DealSource(), this.dealUrl(), this.pcbpmProcessId(), this.mainLineOfBusinessId()];
    return values.some(value => !!value);
  });

  isOtherCompany = toSignal(
    this.assessmentForm.controls.company.valueChanges.pipe(
      map(value => value?.sdlId === this.OTHER_COMPANY_ID),
      tap(isOther => {
        if (isOther) {
          this.assessmentForm.controls.otherCompany.setValidators([Validators.required, requiredTrimmed()]);
        } else {
          this.assessmentForm.controls.otherCompany.clearValidators();
          this.assessmentForm.controls.otherCompany.updateValueAndValidity();
        }
      })
    )
  );
  isOtherProject = toSignal(this.assessmentForm.controls.project.valueChanges.pipe(map(value => value?.sdlId === this.OTHER_PROJECT_ID)));

  readonly viewportRef = viewChild(CdkVirtualScrollViewport);

  get coordinatesFormArray(): FormArray {
    return this.assessmentForm.get('protectedAreaCoordinates') as FormArray;
  }

  private nextId = 0; // Used to generate unique IDs for coordinate groups
  // Observable driving the virtual scroll
  coordinates$ = new BehaviorSubject<AbstractControl[]>([]);
  coordinateControls = toSignal(this.coordinates$);
  getViewportHeight = computed(() => {
    const count = this.coordinateControls()?.length || 0;
    const itemSize = 85; // considering the height of error message
    const maxHeight = 250;
    return Math.min(count * itemSize, maxHeight);
  });
  isTopLimit = computed(() => {
    return this.coordinateControls()?.length === this.MAX_COORDINATES;
  });
  isAtLeastOneCoordinate = computed(() => {
    return (this.coordinateControls()?.length || 0) > 0;
  });
  numberOfCoordinatesText = computed(() => {
    return `Total: ${this.coordinateControls()?.length || 0} entries`;
  });

  constructor() {
    this.initSectorControl();
    effect(() => {
      if (!this.isCompanyLoading() && this.assessmentForm.controls.sector.value) {
        this.assessmentForm.controls.company.enable();
      }
    });

    this.initCountryControl();
    effect(() => {
      if (!this.isProjectLoading() && this.assessmentForm.controls.countries.valid) {
        this.assessmentForm.controls.project.enable();
      }
    });

    this.initMutualExclusivity();
    this.fillFormWithQueryParams();
  }

  ngOnDestroy(): void {
    this.messageService.dismiss();
  }

  private initMutualExclusivity(): void {
    this.assessmentForm.controls.isThirdParty.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
      if (value) this.assessmentForm.controls.isLH.setValue(false, { emitEvent: false });
    });

    this.assessmentForm.controls.isLH.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
      if (value) this.assessmentForm.controls.isThirdParty.setValue(false, { emitEvent: false });
    });
  }

  private initSectorControl() {
    this.assessmentForm.controls.sector.valueChanges
      .pipe(
        tap(value => {
          this.assessmentForm.controls.company.setValue(undefined);
          this.assessmentForm.controls.company.disable();
          if (value) this.store.loadCompanies(value.sdlId);
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  private initCountryControl() {
    this.assessmentForm.controls.countries.valueChanges
      .pipe(
        tap(() => {
          this.assessmentForm.controls.project.setValue(undefined);
          this.assessmentForm.controls.project.disable();
        }),
        filter((value): value is SdlIdName[] => Array.isArray(value) && value.length > 0 && value.length <= this.MAX_SELECTION),
        tap(value => this.store.loadProjects(value)),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  private fillFormWithQueryParams() {
    // fill form with countries from query params
    effect(() => {
      const countryIds = this.Country()?.split(';') ?? [];
      const countries = this.countries().filter(country => countryIds.includes(country.sdlId.toString()));
      if (countries.length) {
        this.assessmentForm.controls.countries.patchValue(countries);
      }
    });

    // fill form with sector from query params
    effect(() => {
      const sectorIdOrMdmId = this.Sector() ?? this.Industry();
      const sector = this.sectors().find(s => s.sdlId == Number(sectorIdOrMdmId) || s.mdmId == Number(sectorIdOrMdmId));
      if (sector) {
        this.assessmentForm.controls.sector.patchValue(sector);
      }
    });

    // fill form with company from query params
    effect(() => {
      const company = this.companies().find(c => c.sdlId === Number(this.Company()));
      if (company) {
        this.assessmentForm.controls.company.patchValue(company);
      }
      if (company?.sdlId === this.OTHER_COMPANY_ID) {
        const otherCompanyVal = this.otherCompany();
        if (otherCompanyVal) {
          this.assessmentForm.controls.otherCompany.patchValue(otherCompanyVal ?? '');
        }
      }
    });

    // fill form with project from query params
    effect(() => {
      const project = this.projects().find(p => p.sdlId === Number(this.Project()));
      if (project) {
        this.assessmentForm.controls.project.patchValue(project);
      }
      if (project?.sdlId === this.OTHER_PROJECT_ID) {
        const otherProjectVal = this.otherProject();
        if (otherProjectVal) {
          this.assessmentForm.controls.otherProject.patchValue(otherProjectVal ?? '');
        }
      }
    });

    // fill form with attributes from query params not bound with data
    effect(() => {
      this.assessmentForm.patchValue({
        dealSourceSystemId: this.DealID(),
        dealUrl: this.dealUrl(),
        dealSourceSystem: this.DealSource() ?? this.SOURCE_SYSTEM,
      });
    });
  }

  onSubmit() {
    this.store.createAssessment$({
      ...this.assessmentForm.getRawValue(),
      mainLineOfBusinessId: Number(this.mainLineOfBusinessId()) || undefined,
      pcbpmProcessId: this.pcbpmProcessId(),
    } as AssessmentCreation);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasLinkData() && !this.isSubmittedSuccessfully()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  confirmLeave(): boolean | Observable<boolean> {
    if (!this.hasLinkData() || this.isSubmittedSuccessfully()) {
      return true;
    }

    if (this.activeDialogRef) {
      return this.activeDialogRef.afterClosed().pipe(map(result => result === true));
    }

    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, discardLinkDialogData);
    this.activeDialogRef = dialogRef;

    return dialogRef.afterClosed().pipe(
      map(result => result === true),
      tap(() => (this.activeDialogRef = undefined))
    );
  }

  private coordinateRangeValidator(min: number, max: number) {
    return (control: AbstractControl) => {
      const raw = control.value;
      const num = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isFinite(num)) return { notNumber: true };
      if (num < min || num > max) return { outOfRange: { min, max } };
      return null;
    };
  }

  createCoordinateGroup(propertyName?: string, latitude?: number, longitude?: number): FormGroup {
    const group = this.fb.group({
      __id: this.nextId++,
      propertyName: [propertyName, Validators.required],
      latitude: [latitude, [Validators.required, this.coordinateRangeValidator(-90, 90)]],
      longitude: [longitude, [Validators.required, this.coordinateRangeValidator(-180, 180)]],
    });
    if (latitude || longitude || propertyName) {
      group.get('latitude')?.markAsTouched();
      group.get('longitude')?.markAsTouched();
      group.get('propertyName')?.markAsTouched();
    }
    return group;
  }

  addCoordinateGroup(propertyName?: string, latitude?: number, longitude?: number): void {
    this.coordinatesFormArray.push(this.createCoordinateGroup(propertyName, latitude, longitude));
    this.coordinates$.next([...this.coordinatesFormArray.controls]);

    setTimeout(() => {
      this.viewportRef()?.checkViewportSize?.();
      this.scrollToLastCoordinate();
    });
  }

  // Remove a coordinate group at specific index
  removeCoordinateGroup(index: number): void {
    this.coordinatesFormArray.removeAt(index);
    // Emit new array reference again
    this.coordinates$.next([...this.coordinatesFormArray.controls]);

    setTimeout(() => {
      this.viewportRef()?.checkViewportSize?.();
    });
  }

  private scrollToLastCoordinate(): void {
    const length = this.coordinatesFormArray.length;
    const viewport = this.viewportRef();
    if (viewport && length > 0) {
      viewport.scrollToIndex(length - 1, 'smooth');
    }
  }

  addBulkCoordinates(event: Event, fileInput: HTMLInputElement) {
    this.isBulkCoordinatesLoading.set(true);
    const files = (event.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      const file = files[0];
      // check file format
      const fileFormat = file.name.split('.').pop()?.toLowerCase();
      if (fileFormat !== 'xls' && fileFormat !== 'xlsx') {
        this.messageService.showInfo('Only Excel files (.xls, .xlsx) are supported for bulk upload.');
        this.isBulkCoordinatesLoading.set(false);
        return;
      }
      this.processBulkCoordinates(file);
      fileInput.value = '';
    }
  }

  removeAllCoordinateGroups(): void {
    this.coordinatesFormArray.clear();
    this.coordinates$.next([]);
    setTimeout(() => {
      this.viewportRef()?.checkViewportSize?.();
    });
  }

  private processBulkCoordinates(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const data = new Uint8Array(reader.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const raw_data: (string | number)[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: false });

      const numberOfCoordinates = (this.coordinateControls()?.length || 0) + raw_data.length - 1;
      if (numberOfCoordinates > this.MAX_COORDINATES) {
        const snackData: SnackWithLinksData = {
          segments: [
            {
              type: 'text',
              text: `You have reached the maximum limit of ${this.MAX_COORDINATES} locations. Please reduce the number by removing immaterial locations, or alternatively use `,
            },
            { type: 'link', label: 'CatNet Internal', href: environment.catNet.baseUrl, target: '_blank', rel: 'noopener' },
            { type: 'text', text: ` for the protected areas check by selecting the 'ESG Protected Areas' layer.` },
          ],
        };
        this.messageService.showWithComponent(SnackbarWithLinksComponent, snackData);
      } else {
        if (!this.validHeaders(raw_data[0] as string[])) {
          this.messageService.showInfo(
            'Invalid file format. Please ensure the excel file has headers: Property Name, Latitude, Longitude.'
          );
          this.isBulkCoordinatesLoading.set(false);
          return;
        }
        // Skip the header row (index 0) and process data rows
        for (const row of raw_data.slice(1)) {
          const propertyName = row[0]?.toString().trim() || '';
          const latitude = parseFloat(row[1]?.toString() || '');
          const longitude = parseFloat(row[2]?.toString() || '');
          this.addCoordinateGroup(propertyName, latitude, longitude);
        }
      }

      this.isBulkCoordinatesLoading.set(false);
    };
    reader.readAsArrayBuffer(file);
  }

  private validHeaders(headers: string[]): boolean {
    return headers.length == 3 && headers[0] === 'Property Name' && headers[1] === 'Latitude' && headers[2] === 'Longitude';
  }

  trackByGroupId(index: number, item: AbstractControl): number {
    const group = item as FormGroup;
    return group.get('__id')!.value;
  }

  downloadPATemplate() {
    const filename = 'Protected_Area_template.xlsx';
    const filePath = `assets/files/${filename}`;
    downloadFileFromAssetFolder(filePath, filename);
  }
}
